import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker } from "../../../../components/DateRangePicker";
import { Dropdown } from "../../../../components/Dropdown";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { callExportFilename, exportCalls } from "../../../../services/calls";
import type { CallStatus, OrganizationMember } from "../../../../types/api";
import {
  calendarDaysBetween,
  toIsoDate,
  todayCalendarDate,
  type CalendarDateRange,
} from "../../../../utils/calendarDate";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { cycleTabFocus } from "../../../../utils/focus-trap";
import { downloadBlobFile } from "../../../../utils/file-download";
import "./CallModal.css";
import "./CallExportModal.css";

/** Mirrors CallReportRange.MaxDays on the API, so an over-long range is caught before the request. */
const MAX_RANGE_DAYS = 366;

/** The date picker's portalled panel, when it is open. Null otherwise. */
const pickerPanel = (): HTMLElement | null =>
  document.querySelector<HTMLElement>("[data-picker-panel]");

type StatusFilter = CallStatus | "all";

// Annotated, not inferred: the values are the wire contract. Without this the option list
// could drift from CallStatus and only fail at runtime, as a 400 from the API.
const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: "All", value: "all" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Complete", value: "complete" },
];

/** The Dropdown hands back a bare string; this is where it re-enters the typed world. */
const isStatusFilter = (value: string): value is StatusFilter =>
  STATUS_OPTIONS.some((option) => option.value === value);

interface CallExportModalProps {
  /** Assignable coaches, already loaded by the list. */
  coaches: OrganizationMember[];
  onClose: () => void;
}

/** This month to date — the range a coach filing contact activity reaches for most often. */
const defaultRange = (): CalendarDateRange => {
  const today = todayCalendarDate();
  return { start: { ...today, day: 1 }, end: today };
};

/**
 * Downloads the call log for a date range as an Excel workbook.
 *
 * Exists because college staff have to file their contact activity with a compliance service
 * (Arms). They need the calls for a stated window — with the evaluations written about those
 * players, handwritten ones included — as a spreadsheet they can attach to an email, rather
 * than something they retype off the screen.
 */
export function CallExportModal({ coaches, onClose }: CallExportModalProps) {
  const [range, setRange] = useState<CalendarDateRange>(defaultRange);
  const [coachId, setCoachId] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape to close, plus a Tab trap: the dialog declares aria-modal, which tells assistive
  // tech the page behind it is inert, so Tab must not actually walk into the calls table.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (event.key === "Escape") {
        // Not while a download is in flight: closing would strand the request with no
        // feedback. The date picker's own panel stops Escape before it reaches here, so
        // closing the picker never also closes the dialog.
        if (!exporting) onClose();
        return;
      }

      if (event.key !== "Tab") return;

      // The picker's panel is portalled to the body, so it is outside the dialog in the DOM
      // but part of it to the user. While focus is in there the panel owns the keyboard.
      const active = document.activeElement;
      if (active instanceof HTMLElement && pickerPanel()?.contains(active)) return;

      cycleTabFocus(dialog, event);
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [exporting, onClose]);

  // Move focus into the dialog on open and hand it back to the Export button on close.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
  }, []);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget && !exporting) onClose();
    },
    [onClose, exporting]
  );

  const coachOptions = useMemo(
    () => [
      { label: "All coaches", value: "" },
      ...coaches.map((coach) => ({ label: coach.displayName, value: coach.id })),
    ],
    [coaches]
  );

  const dayCount =
    range.start && range.end ? calendarDaysBetween(range.start, range.end) : 0;

  const rangeError =
    range.start && range.end && dayCount > MAX_RANGE_DAYS
      ? `Choose a range of ${MAX_RANGE_DAYS} days or fewer.`
      : undefined;

  const canExport = !!range.start && !!range.end && !rangeError && !exporting;

  const handleExport = async () => {
    if (!range.start || !range.end) return;

    const startDate = toIsoDate(range.start);
    const endDate = toIsoDate(range.end);

    setExporting(true);
    setError(null);
    try {
      const blob = await exportCalls({
        startDate,
        endDate,
        coachId: coachId || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
      });
      downloadBlobFile(blob, callExportFilename(startDate, endDate));
      onClose();
    } catch (err) {
      // Stay open on failure so the coach can narrow the range and retry rather than
      // reopening the dialog and re-entering everything.
      setError(getSafeErrorMessage(err, "Failed to export calls"));
      console.error("Failed to export calls:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="schedule-call-modal__overlay" onClick={handleOverlayClick}>
      <div
        className="schedule-call-modal__content"
        ref={dialogRef}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-export-modal-title"
      >
        <div className="schedule-call-modal__header">
          <div className="schedule-call-modal__header-content">
            <h3 id="call-export-modal-title" className="schedule-call-modal__title">
              Export Calls
            </h3>
            <p className="schedule-call-modal__subtitle">
              Download a spreadsheet of calls, evaluations, and handwritten notes
              for a date range.
            </p>
          </div>
          <ModalCloseButton onClick={onClose} disabled={exporting} />
        </div>

        <div className="schedule-call-modal__body">
          <div className="call-export-modal__field">
            <DateRangePicker
              id="call-export-range"
              label="Date range"
              value={range}
              onChange={setRange}
              error={rangeError}
              disabled={exporting}
              testId="call-export-range"
            />
          </div>

          <div className="call-export-modal__filters">
            <Dropdown
              label="Coach"
              options={coachOptions}
              value={coachId}
              onSelect={setCoachId}
              placeholder="All coaches"
            />
            <Dropdown
              label="Status"
              options={STATUS_OPTIONS}
              value={statusFilter}
              onSelect={(value) => {
                if (isStatusFilter(value)) setStatusFilter(value);
              }}
            />
          </div>

          {/* Says what the file will contain before it is generated — a report that turns out
              to cover the wrong window is only discovered after it has been emailed. */}
          {range.start && range.end && !rangeError && (
            <p className="call-export-modal__summary" data-testid="call-export-summary">
              {dayCount === 1 ? "1 day" : `${dayCount} days`} of calls, plus the
              evaluations written about those players — handwritten notes included
              as images.
            </p>
          )}

          {error && (
            <div className="schedule-call-modal__error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="schedule-call-modal__footer">
          <button
            type="button"
            className="schedule-call-modal__cancel-button"
            onClick={onClose}
            disabled={exporting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="schedule-call-modal__save-button"
            data-testid="call-export-submit"
            onClick={handleExport}
            disabled={!canExport}
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}
